import { api } from "./client";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getCurrentLang = () => {
  const candidates = [
    localStorage.getItem("lang"),
    localStorage.getItem("language"),
    localStorage.getItem("i18n_lang"),
    localStorage.getItem("i18nextLng"),
    typeof window !== "undefined" ? window.lang : "",
    typeof document !== "undefined" ? document.documentElement.lang : "",
    typeof navigator !== "undefined" ? navigator.language : "",
  ];
  const hasArabic = candidates.some((candidate) =>
    String(candidate || "")
      .toLowerCase()
      .startsWith("ar")
  );

  if (hasArabic) return "ar";

  if (
    typeof document !== "undefined" &&
    String(document.documentElement?.dir || "").toLowerCase() === "rtl"
  ) {
    return "ar";
  }

  return "en";
};

const getAuthHeaders = () => {
  const lang = getCurrentLang();
  const token = localStorage.getItem("token");
  const headers = {
    "Accept-Language": lang,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const buildReportParams = (params = {}) => {
  const allowed = ["search", "startDate", "endDate", "warehouse", "page", "limit"];
  return allowed.reduce((acc, key) => {
    if (typeof params[key] !== "undefined" && params[key] !== null && params[key] !== "") {
      acc[key] = params[key];
    }
    return acc;
  }, {});
};

const buildExportParams = (params = {}) => {
  const lang = getCurrentLang();
  const baseParams = buildReportParams(params);

  return {
    ...baseParams,
    lang,
    language: lang,
    locale: lang,
    lng: lang,
    isArabic: Number(lang === "ar"),
    rtl: Number(lang === "ar"),
  };
};

const getLocalizedText = (value, lang = getCurrentLang()) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value?.[lang] || value?.en || value?.ar || "";
  }
  return String(value);
};

export const getPurchaseOrderReport = async (params = {}) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}api/purchase-order-report`,
      {
        headers: getAuthHeaders(),
        params: buildReportParams(params),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Get Purchase Order Report API Error:", error);
    throw (
      error?.response?.data || {
        message: "Failed to fetch purchase order report",
      }
    );
  }
};

export const getPurchaseOrderReportSummary = async (params = {}) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}api/purchase-order-report/summary`,
      {
        headers: getAuthHeaders(),
        params: buildReportParams(params),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Get Purchase Order Report Summary API Error:", error);
    throw (
      error?.response?.data || {
        message: "Failed to fetch purchase order report summary",
      }
    );
  }
};

export const downloadPurchaseOrderReportExcel = async (params = {}) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}api/purchase-order-report/download/excel`,
      {
        headers: getAuthHeaders(),
        params: buildExportParams(params),
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "purchase-order-report.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Download Purchase Order Report Excel API Error:", error);
    throw (
      error?.response?.data || {
        message: "Failed to download excel report",
      }
    );
  }
};

export const downloadPurchaseOrderReportPdf = async (params = {}) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}api/purchase-order-report/download/pdf`,
      {
        headers: getAuthHeaders(),
        params: buildExportParams(params),
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "purchase-order-report.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Download Purchase Order Report PDF API Error:", error);
    throw (
      error?.response?.data || {
        message: "Failed to download pdf report",
      }
    );
  }
};

export const mapPurchaseOrderReportRow = (row) => {
  const lang = getCurrentLang();
  const productName =
    getLocalizedText(row?.productName, lang) ||
    getLocalizedText(row?.name, lang) ||
    getLocalizedText(row?.product?.productName, lang) ||
    getLocalizedText(row?.product?.name, lang) ||
    "-";

  return {
    key: row?.key || row?.productId,
    productId: row?.productId || "",
    productName,
    img: row?.image || "",
    sku: row?.sku || "",
    itemCode: row?.itemCode || "",
    productAmount: Number(row?.productAmount || 0),
    productAmountLabel: `$${Number(row?.productAmount || 0).toFixed(2)}`,
    productQty: Number(row?.productQty || 0),
    instockQty: Number(row?.instockQty || 0),
    purchaseCount: Number(row?.purchaseCount || 0),
    lastPurchaseDate: row?.lastPurchaseDate || null,
    raw: row,
  };
};
